import { createTheme } from "@mui/material/styles";

// Helper — CSS variable to use in MUI
const v = (variable) => `var(${variable})`;

const theme = createTheme({
  palette: {
    primary: {
      main: "#301aff",
      light: "rgba(48, 26, 255, 0.1)",
      contrastText: "#ffffff",
    },
    success: {
      main: "#34c759",
    },
    warning: {
      main: "#ffcc00",
    },
    error: {
      main: "#ff3b30",
    },
    background: {
      // Use concrete values here to avoid passing CSS vars into palette
      // utilities that may call color helpers (e.g. alpha()).
      default: "#fbf9f3",
      paper: "#ffffff",
    },
    text: {
      // Keep palette text values as concrete colors because MUI utilities
      // (like alpha()) cannot process CSS variables here.
      primary: "#000000",
      secondary: "#5f6368",
      disabled: "#acacac",
    },
  },

  typography: {
    fontFamily: v("--font-primary"),
    fontSize: 14,
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },

  shape: {
    borderRadius: 8,
  },

  shadows: [
    "none",
    v("--shadow-sm"),
    v("--shadow-md"),
    v("--shadow-lg"),
    v("--shadow-xl"),
    ...Array(20).fill("none"),
  ],

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: v("--radius-md"),
          padding: `${v("--space-sm")} ${v("--space-lg")}`,
          fontSize: v("--text-base"),
          fontWeight: 600,
          boxShadow: "none",
          transition: v("--transition"),
          "&:hover": {
            boxShadow: "none",
          },
        },
        containedPrimary: {
          backgroundColor: v("--btn-purple-color"),
          "&:hover": {
            backgroundColor: v("--btn-purple-hover"),
          },
          "&:active": {
            backgroundColor: v("--btn-purple-active"),
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: v("--radius-lg"),
          boxShadow: v("--shadow-sm"),
          border: `1px solid ${v("--border-color-light")}`,
          backgroundColor: v("--white"),
          transition: v("--transition"),
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: v("--radius-sm"),
          fontSize: v("--text-sm"),
          fontWeight: 600,
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${v("--border-color-light")}`,
          borderRadius: v("--radius-lg"),
          backgroundColor: v("--white"),
          fontSize: v("--text-base"),
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: v("--theme-gray-bg"),
            borderBottom: `1px solid ${v("--border-color-light")}`,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: v("--theme-color-secondary"),
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${v("--border-color-light")}`,
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: v("--text-base"),
          color: "#7c7c7c",
          transition: v("--transition"),
          "&.Mui-selected": {
            color: "#301aff",
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: v("--btn-purple-color"),
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: v("--text-base"),
          borderRadius: v("--radius-md"),
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: v("--radius-md"),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: v("--border-color-light"),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: v("--border-color-light-dark"),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: v("--btn-purple-color"),
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: v("--gray-darkest"),
          fontSize: v("--text-xs"),
          borderRadius: v("--radius-sm"),
          padding: `${v("--space-xs")} ${v("--space-sm")}`,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: v("--border-color-light"),
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: v("--white"),
          borderRadius: v("--radius-lg"),
        },
        elevation1: { boxShadow: v("--shadow-sm") },
        elevation2: { boxShadow: v("--shadow-md") },
        elevation3: { boxShadow: v("--shadow-lg") },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: v("--text-base"),
          borderBottom: `1px solid ${v("--border-color-light")}`,
          padding: `${v("--space-sm")} ${v("--space-md")}`,
        },
        head: {
          fontWeight: 600,
          backgroundColor: v("--theme-gray-bg"),
          color: v("--text-color-primary"),
        },
      },
    },
  },
});

export default theme;
